import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ChangePassword.css';

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const checkPasswordRequirement = (requirement: string): boolean => {
    switch (requirement) {
      case 'length':
        return newPassword.length >= 8;
      case 'capital':
        return /[A-Z]/.test(newPassword);
      case 'lowercase':
        return /[a-z]/.test(newPassword);
      case 'number':
        return /[0-9]/.test(newPassword);
      case 'symbol':
        return /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
      default:
        return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!oldPassword) {
      alert('Please enter your current password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    
    const allRequirementsMet = ['length', 'capital', 'lowercase', 'number', 'symbol']
      .every(req => checkPasswordRequirement(req));
    
    if (!allRequirementsMet) {
      alert('Please meet all password requirements');
      return;
    }
    
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      navigate('/profile-security');
    }, 2000);
  };

  const handleBack = () => {
    navigate('/profile-security');
  };

  return (
    <div className="change-password-container">
      <div className="change-password-header">
        <button className="change-password-back-button" onClick={handleBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="change-password-header-title">Change Password</h1>
      </div>

      <div className="change-password-content">
        <form onSubmit={handleSubmit} className="change-password-form">
          <div className="change-password-field">
            <label className="change-password-label">Old password</label>
            <div className="change-password-input-wrapper">
              <input
                type={showOldPassword ? 'text' : 'password'}
                className="change-password-input"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="••••••••••••"
                required
              />
              <button
                type="button"
                className="change-password-toggle"
                onClick={() => setShowOldPassword(!showOldPassword)}
                aria-label={showOldPassword ? 'Hide password' : 'Show password'}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  {showOldPassword ? (
                    <path d="M17.5 10C17.5 10 14.375 15 10 15C5.625 15 2.5 10 2.5 10C2.5 10 5.625 5 10 5C14.375 5 17.5 10 17.5 10Z M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  ) : (
                    <>
                      <path d="M17.5 10C17.5 10 14.375 15 10 15C5.625 15 2.5 10 2.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2.5 10C2.5 10 5.625 5 10 5C14.375 5 17.5 10 17.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>

          <div className="change-password-field">
            <label className="change-password-label">New password</label>
            <div className="change-password-input-wrapper">
              <input
                type={showNewPassword ? 'text' : 'password'}
                className="change-password-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••••••"
                required
              />
              <button
                type="button"
                className="change-password-toggle"
                onClick={() => setShowNewPassword(!showNewPassword)}
                aria-label={showNewPassword ? 'Hide password' : 'Show password'}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  {showNewPassword ? (
                    <path d="M17.5 10C17.5 10 14.375 15 10 15C5.625 15 2.5 10 2.5 10C2.5 10 5.625 5 10 5C14.375 5 17.5 10 17.5 10Z M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  ) : (
                    <>
                      <path d="M17.5 10C17.5 10 14.375 15 10 15C5.625 15 2.5 10 2.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2.5 10C2.5 10 5.625 5 10 5C14.375 5 17.5 10 17.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>

          <div className="change-password-requirements">
            <div className="requirements-row">
              <span className={`requirement-badge ${checkPasswordRequirement('length') ? 'met' : ''}`}>
                Min. 8 characters
              </span>
              <span className={`requirement-badge ${checkPasswordRequirement('capital') ? 'met' : ''}`}>
                Capital
              </span>
            </div>
            <div className="requirements-row">
              <span className={`requirement-badge ${checkPasswordRequirement('lowercase') ? 'met' : ''}`}>
                Lowercase
              </span>
              <span className={`requirement-badge ${checkPasswordRequirement('number') ? 'met' : ''}`}>
                Number
              </span>
              <span className={`requirement-badge ${checkPasswordRequirement('symbol') ? 'met' : ''}`}>
                Symbol
              </span>
            </div>
          </div>

          <div className="change-password-field">
            <label className="change-password-label">Confirm password</label>
            <div className="change-password-input-wrapper">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="change-password-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••••••"
                required
              />
              <button
                type="button"
                className="change-password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  {showConfirmPassword ? (
                    <path d="M17.5 10C17.5 10 14.375 15 10 15C5.625 15 2.5 10 2.5 10C2.5 10 5.625 5 10 5C14.375 5 17.5 10 17.5 10Z M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  ) : (
                    <>
                      <path d="M17.5 10C17.5 10 14.375 15 10 15C5.625 15 2.5 10 2.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2.5 10C2.5 10 5.625 5 10 5C14.375 5 17.5 10 17.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>

          <button type="submit" className="change-password-submit-button">
            Reset Password
          </button>
        </form>
      </div>

      {showSuccess && (
        <div className="change-password-success-notification">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path 
              d="M16.667 5L7.5 14.167L3.333 10" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
          <span>Password changed successfully!</span>
        </div>
      )}
    </div>
  );
};

export default ChangePassword;
